import { useEffect, useState, useCallback, useRef } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusBadge, PriorityBadge, statusStripClass, OverdueBadge, OutageBadge } from "@/components/app/Badges";
import ComplaintDetailDialog from "@/components/app/ComplaintDetailDialog";
import RaiseComplaintDialog from "@/components/app/RaiseComplaintDialog";
import OutageBanner from "@/components/app/OutageBanner";
import { toast } from "sonner";
import { Search, MapPin, Clock, Wrench, Plus, Inbox, Bell } from "lucide-react";

function urlB64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const s = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(s);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export default function TechnicianDashboard() {
  const { user, config } = useAuth();
  const isAllArea = !user?.location;
  const [mine, setMine] = useState([]);
  const [available, setAvailable] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("mine");
  const [selected, setSelected] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const [claiming, setClaiming] = useState(null);
  const [notifPerm, setNotifPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "denied");
  const prevAvailableIdsRef = useRef(null);

  async function askNotifPerm() {
    if (typeof Notification === "undefined") { toast.error("Notifications not supported on this device"); return; }
    const p = await Notification.requestPermission();
    setNotifPerm(p);
    if (p !== "granted") { toast.error("Notification permission denied"); return; }
    try {
      const reg = await navigator.serviceWorker.ready;
      const { data: cfg } = await api.get("/push/public-key");
      if (!cfg.enabled || !cfg.key) { toast.info("Server push not configured — in-tab pings only"); return; }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(cfg.key),
      });
      const raw = sub.toJSON();
      await api.post("/push/subscribe", { endpoint: raw.endpoint, keys: raw.keys });
      toast.success("Push alerts enabled — you'll be pinged even when the app is closed");
    } catch (e) {
      console.warn("push subscribe failed", e);
      toast.info("Push subscribe failed — using in-tab pings only");
    }
  }

  const load = useCallback(async () => {
    try {
      const params = { q: q || undefined };
      const mineRes = await api.get("/complaints", { params });
      setMine(mineRes.data);
      const availRes = await api.get("/complaints", { params: { ...params, available: true } });
      const items = availRes.data;
      // Push-notification hook: fire local notifications for newly-appeared tickets.
      if (prevAvailableIdsRef.current !== null && typeof Notification !== "undefined" && Notification.permission === "granted") {
        const prev = prevAvailableIdsRef.current;
        const fresh = items.filter((c) => !prev.has(c.id));
        fresh.slice(0, 3).forEach((c) => {
          try {
            const n = new Notification(`New ticket · ${c.location}`, {
              body: `${c.complaint_type} — ${c.customer_name}${c.customer_account_id ? " · " + c.customer_account_id : ""}`,
              icon: "/icons/icon-192.png",
              badge: "/icons/icon-192.png",
              tag: c.id,
            });
            n.onclick = () => { window.focus(); setTab("available"); setSelected(c.id); n.close(); };
          } catch {}
        });
      }
      prevAvailableIdsRef.current = new Set(items.map((c) => c.id));
      setAvailable(items);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  }, [q]);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  async function claim(id) {
    setClaiming(id);
    try {
      await api.patch(`/complaints/${id}`, { assigned_to: user.id });
      toast.success("Ticket claimed — moved to My Queue");
      setTab("mine");
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setClaiming(null);
    }
  }

  const assigned = mine.filter((i) => i.status === "Assigned").length;
  const inp = mine.filter((i) => i.status === "In Progress").length;
  const resolved = mine.filter((i) => i.status === "Resolved").length;

  return (
    <AppShell
      title={`Field Dispatch · ${user?.name || ""}`}
      subtitle={isAllArea ? "All-area supervisor view · every ticket across Goa" : `Working area · ${user?.location}`}
      nav={[{ to: "/tech", label: "My Queue" }]}
    >
      <div className="mb-4 flex flex-wrap gap-2">
        {isAllArea && (
          <div data-testid="all-area-banner" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs font-mono-alt uppercase tracking-wider text-purple-300 border border-purple-500/30">
            <MapPin className="w-3 h-3" /> All-area access
          </div>
        )}
        {notifPerm !== "granted" && (
          <button
            data-testid="enable-notifs-btn"
            onClick={askNotifPerm}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-mono-alt uppercase tracking-wider bg-amber-500/15 text-amber-200 border border-amber-500/40 hover:bg-amber-500/25"
          >
            <Bell className="w-3 h-3" /> Enable push alerts
          </button>
        )}
      </div>

      <OutageBanner role="technician" onChanged={load} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat icon={<Wrench className="w-5 h-5" />} label="Assigned" value={assigned} tint="blue" />
        <Stat icon={<Clock className="w-5 h-5" />} label="In Progress" value={inp} tint="indigo" />
        <Stat icon={<MapPin className="w-5 h-5" />} label="Resolved" value={resolved} tint="emerald" />
        <Stat icon={<Inbox className="w-5 h-5" />} label="Available" value={available.length} tint="amber" />
      </div>

      <div className="flex flex-wrap justify-end mb-3">
        <Button data-testid="tech-raise-complaint-btn" onClick={() => setOpenNew(true)} className="bg-purple-500 hover:bg-purple-600">
          <Plus className="w-4 h-4 mr-1.5" /> Register complaint
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-900 border border-slate-800 mb-4">
          <TabsTrigger data-testid="tech-tab-mine" value="mine">My Queue ({mine.length})</TabsTrigger>
          <TabsTrigger data-testid="tech-tab-available" value="available">Available ({available.length})</TabsTrigger>
        </TabsList>

        <div className="tactical-card p-4 mb-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="tech-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tickets…" className="bg-slate-900 border-slate-700 pl-9" />
          </div>
        </div>

        <TabsContent value="mine">
          <TicketList items={mine} onOpen={setSelected} emptyText={isAllArea ? "No complaints across any area yet." : "No assigned tickets yet."} testidPrefix="tech-row" overdueHours={config?.overdue_hours ?? 4} />
        </TabsContent>

        <TabsContent value="available">
          <TicketList
            items={available}
            onOpen={setSelected}
            emptyText="No unassigned tickets in your area."
            testidPrefix="avail-row"
            overdueHours={config?.overdue_hours ?? 4}
            actionBtn={(c) => (
              <Button
                data-testid={`claim-btn-${c.ticket_no}`}
                disabled={claiming === c.id}
                onClick={(e) => { e.stopPropagation(); claim(c.id); }}
                size="sm"
                className="bg-purple-500 hover:bg-purple-600"
              >
                <Wrench className="w-3.5 h-3.5 mr-1" /> Assign to me
              </Button>
            )}
          />
        </TabsContent>
      </Tabs>

      <ComplaintDetailDialog complaintId={selected} open={!!selected} onOpenChange={(v) => !v && setSelected(null)} onChanged={load} />
      <RaiseComplaintDialog open={openNew} onOpenChange={setOpenNew} onCreated={load} />
    </AppShell>
  );
}

function TicketList({ items, onOpen, emptyText, testidPrefix, actionBtn, overdueHours = 4 }) {
  if (items.length === 0) {
    return <div className="tactical-card p-10 text-center text-slate-400">{emptyText}</div>;
  }
  return (
    <div className="grid gap-3">
      {items.map((c) => (
        <button
          key={c.id}
          data-testid={`${testidPrefix}-${c.ticket_no}`}
          onClick={() => onOpen(c.id)}
          className={`tactical-card p-4 text-left hover:border-purple-500/40 ${statusStripClass(c.status)}`}
        >
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono-alt text-xs text-purple-300 tracking-widest">{c.ticket_no}</span>
                <StatusBadge status={c.status} />
                <PriorityBadge priority={c.priority} />
                <OverdueBadge complaint={c} hours={overdueHours} />
                <OutageBadge complaint={c} />
              </div>
              <div className="font-display font-semibold text-white mt-1.5">
                {c.complaint_type} · <span className="text-slate-300">{c.customer_name}</span>
              </div>
              <div className="text-sm text-slate-400 line-clamp-2">{c.description}</div>
              <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-2 font-mono-alt">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{c.location} — {c.address}</span>
                <span>{new Date(c.created_at).toLocaleString()}</span>
              </div>
            </div>
            {actionBtn && <div className="shrink-0">{actionBtn(c)}</div>}
          </div>
        </button>
      ))}
    </div>
  );
}

function Stat({ icon, label, value, tint }) {
  const tints = {
    blue: "text-purple-300 bg-purple-500/10 border-purple-500/30",
    indigo: "text-indigo-300 bg-indigo-500/10 border-indigo-500/30",
    emerald: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    amber: "text-amber-300 bg-amber-500/10 border-amber-500/30",
  };
  return (
    <div className="tactical-card p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg border flex items-center justify-center ${tints[tint]}`}>{icon}</div>
      <div>
        <div className="text-xs font-mono-alt uppercase tracking-widest text-slate-500">{label}</div>
        <div className="font-display font-bold text-lg text-white">{value}</div>
      </div>
    </div>
  );
}
